import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/app/ui-bits";
import { Input } from "@/components/ui/input";
import { Package, X, MagnifyingGlass } from "@phosphor-icons/react";

/**
 * AssetPicker — searchable asset autocomplete used by every Asset sub-section
 * form (Check In/Out, Mobilisation, Accessories, Calibration, Rentals, Incidents)
 * so users never have to type Asset IDs manually.
 *
 * Props:
 *   value         — currently-selected asset_id (string)
 *   onChange(asset_id, fullAssetObject?)  — callback fired when selection changes
 *   status        — optional status filter (e.g. "in_store") for sourcing only available assets
 *   placeholder   — input placeholder
 *   testid        — data-testid prefix
 *   disabled      — disable selection
 *
 * On select, the parent receives the full asset object as the 2nd argument so
 * downstream forms can auto-fill description, category, location, custodian,
 * etc. without an additional fetch.
 */
export default function AssetPicker({
  value,
  onChange,
  status,
  placeholder = "Search by asset ID, description, serial…",
  testid = "asset-picker",
  disabled = false,
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const ref = useRef(null);

  // If parent provides a value but we don't have a "selected" object yet, fetch it.
  useEffect(() => {
    let alive = true;
    if (!value) { setSelected(null); return; }
    if (selected && selected.asset_id === value) return;
    (async () => {
      try {
        const { data } = await api.get(`/assets/lookup?q=${encodeURIComponent(value)}&limit=5`);
        const match = (data.items || []).find((x) => x.asset_id === value) || (data.items || [])[0];
        if (alive && match) setSelected(match);
      } catch (e) { /* swallow — picker still functions */ }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Debounced live search
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        if (status) params.set("status", status);
        params.set("limit", "25");
        const { data } = await api.get(`/assets/lookup?${params.toString()}`);
        setResults(data.items || []);
      } catch (e) { setResults([]); }
      finally { setLoading(false); }
    }, 180);
    return () => clearTimeout(t);
  }, [q, open, status]);

  // Click-outside to close
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = (asset) => {
    setSelected(asset);
    setOpen(false);
    setQ("");
    onChange?.(asset.asset_id, asset);
  };

  const clear = () => {
    setSelected(null);
    setQ("");
    onChange?.("", null);
  };

  // Render selected state (chip)
  if (selected) {
    return (
      <div ref={ref} className="relative">
        <div
          className="flex items-start gap-2 rounded-md border border-emerald-300 bg-emerald-50/60 p-2.5 text-sm"
          data-testid={`${testid}-selected`}
        >
          <Package size={16} weight="fill" className="mt-0.5 flex-shrink-0 text-emerald-700" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="font-mono-alt text-xs font-bold text-slate-900">{selected.asset_id}</span>
              {selected.category && (
                <span className="font-mono-alt text-[9px] uppercase tracking-wider rounded bg-slate-100 px-1.5 py-0.5 text-slate-700">{selected.category}</span>
              )}
              {selected.status && <StatusBadge status={selected.status === "in_store" ? "active" : selected.status === "deployed" ? "pending" : selected.status === "lost" ? "rejected" : selected.status} />}
            </div>
            <div className="mt-0.5 truncate text-xs text-slate-700">{selected.description}</div>
            {(selected.assigned_to || selected.location) && (
              <div className="mt-0.5 truncate text-[10px] uppercase tracking-wider text-slate-500">
                {selected.assigned_to && <>custodian · {selected.assigned_to}</>}
                {selected.assigned_to && selected.location && " · "}
                {selected.location && <>location · {selected.location}</>}
              </div>
            )}
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={clear}
              className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-rose-600 hover:bg-rose-100"
              title="Change asset"
              data-testid={`${testid}-clear`}
            >
              <X size={12} weight="bold" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // Render search/select state
  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <MagnifyingGlass size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className="pl-8"
          data-testid={`${testid}-input`}
        />
      </div>
      {open && (
        <div className="absolute z-50 mt-1 max-h-80 w-full overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg" data-testid={`${testid}-dropdown`}>
          {loading && <div className="px-3 py-2 text-xs text-slate-500">Searching…</div>}
          {!loading && results.length === 0 && (
            <div className="px-3 py-3 text-xs text-slate-500">
              {q ? "No matching asset found." : "Type to search the Asset Register…"}
            </div>
          )}
          {!loading && results.map((r) => (
            <button
              key={r.id || r.asset_id}
              type="button"
              onClick={() => pick(r)}
              className="block w-full px-3 py-2 text-left hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
              data-testid={`${testid}-opt-${r.asset_id}`}
            >
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="font-mono-alt text-xs font-bold text-slate-900">{r.asset_id}</span>
                {r.category && (
                  <span className="font-mono-alt text-[9px] uppercase tracking-wider rounded bg-slate-100 px-1.5 py-0.5 text-slate-600">{r.category}</span>
                )}
                {r.status && <StatusBadge status={r.status === "in_store" ? "active" : r.status === "deployed" ? "pending" : r.status === "lost" ? "rejected" : r.status} />}
              </div>
              <div className="mt-0.5 truncate text-sm text-slate-800">{r.description}</div>
              {(r.assigned_to || r.location || r.serial_number) && (
                <div className="mt-0.5 truncate text-[10px] uppercase tracking-wider text-slate-500">
                  {r.serial_number && <>sn · {r.serial_number}</>}
                  {r.serial_number && (r.assigned_to || r.location) && " · "}
                  {r.assigned_to && <>{r.assigned_to}</>}
                  {r.assigned_to && r.location && " · "}
                  {r.location && <>{r.location}</>}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
